import React from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { ArrowRight, Zap, Globe2, Activity, ShieldCheck, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import LeadFormSticky from "@/components/site/LeadFormSticky";
import CalendlyButton from "@/components/site/CalendlyButton";
import { trackEvent } from "@/lib/analytics";

const FOREX_IMG = "https://images.pexels.com/photos/6770610/pexels-photo-6770610.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const series = [
  { x: "09:00", v: 1.0810 }, { x: "10:00", v: 1.0822 }, { x: "11:00", v: 1.0815 },
  { x: "12:00", v: 1.0835 }, { x: "13:00", v: 1.0844 }, { x: "14:00", v: 1.0856 },
  { x: "15:00", v: 1.0871 }, { x: "16:00", v: 1.0865 }, { x: "17:00", v: 1.0892 },
];

const pairs = [
  { pair: "EUR / USD", price: "1.0892", chg: "+0.34%", up: true },
  { pair: "GBP / USD", price: "1.2671", chg: "-0.12%", up: false },
  { pair: "USD / JPY", price: "151.23", chg: "+0.08%", up: true },
  { pair: "AUD / USD", price: "0.6582", chg: "+0.45%", up: true },
  { pair: "USD / CAD", price: "1.3641", chg: "-0.21%", up: false },
  { pair: "USD / CHF", price: "0.8975", chg: "+0.12%", up: true },
  { pair: "USD / INR", price: "83.41", chg: "-0.05%", up: false },
  { pair: "XAU / USD", price: "2,378.40", chg: "+1.04%", up: true },
];

export default function ForexPage() {
  return (
    <div data-testid="forex-page">
      <LeadFormSticky source="forex" />
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <img src={FOREX_IMG} alt="Forex trading" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030712]/80 via-[#030712]/85 to-[#030712]" />
        <div className="mfb-container relative">
          <div className="overline mb-4">Forex Trading</div>
          <h1 className="font-[Outfit] text-5xl sm:text-6xl font-light tracking-tighter text-white max-w-4xl leading-[1.05]">
            <span className="gradient-text-sky">60+ currency pairs.</span><br />
            Institutional spreads. 24/5 markets.
          </h1>
          <p className="mt-6 max-w-2xl text-slate-300 text-base leading-relaxed">
            Trade global currencies, metals and crypto pairs with deep liquidity, lightning execution and smart risk controls — built for serious traders.
          </p>
          <div className="mt-8 flex gap-3">
            <Link
              to="/contact"
              className="btn-primary"
              data-testid="forex-cta-open"
              onClick={() => trackEvent("open_account_click", { source: "forex" })}
            >
              Open Trading Account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/download"
              className="btn-gold"
              data-testid="forex-cta-download"
              onClick={() => trackEvent("download_app_click", { source: "forex" })}
            >
              Download Trading App
            </Link>
            <CalendlyButton label="Book Strategy Call" source="forex" className="btn-ghost" testId="forex-cta-advisor" />
          </div>
        </div>
      </section>

      <section className="mfb-section border-t border-white/5">
        <div className="mfb-container grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 card-luxury p-7">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">EUR / USD</div>
                <div className="font-[Outfit] text-3xl text-white font-light mt-1">1.0892</div>
              </div>
              <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/15">+0.34%</Badge>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={series} margin={{ left: 0, right: 0, top: 20, bottom: 0 }}>
                <defs>
                  <linearGradient id="fxg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="x" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#0B1221", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                <Area type="monotone" dataKey="v" stroke="#10B981" strokeWidth={2.5} fill="url(#fxg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="lg:col-span-5 card-luxury p-2 overflow-hidden">
            <div className="px-5 pt-5 pb-3 flex items-center justify-between">
              <div className="text-sm font-medium text-white">Live Markets</div>
              <Badge className="bg-sky-500/10 text-sky-300 border border-sky-500/20 hover:bg-sky-500/15">Streaming</Badge>
            </div>
            <div className="divide-y divide-white/5">
              {pairs.map((p) => (
                <div key={p.pair} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`pair-${p.pair.replace(/\s|\//g, "")}`}>
                  <div className="text-slate-300">{p.pair}</div>
                  <div className="font-mono text-white">{p.price}</div>
                  <div className={`flex items-center gap-1 text-xs ${p.up ? "text-emerald-400" : "text-rose-400"}`}>
                    {p.up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                    {p.chg}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mfb-section">
        <div className="mfb-container">
          <div className="overline mb-3">Why trade with MFB</div>
          <h2 className="font-[Outfit] text-4xl font-light text-white max-w-2xl">Built for the modern, global currency trader.</h2>
          <div className="grid md:grid-cols-4 gap-5 mt-10">
            {[
              { i: Zap, t: "Ultra-fast execution", d: "Sub-20ms order routing." },
              { i: Globe2, t: "Deep liquidity", d: "Aggregated from 12 top-tier banks." },
              { i: Activity, t: "Smart risk controls", d: "Auto stop-loss, margin alerts." },
              { i: ShieldCheck, t: "Segregated custody", d: "Client funds protected, always." },
            ].map((x, i) => (
              <div key={i} className="card-luxury p-7">
                <x.i className="w-5 h-5 text-sky-400" />
                <div className="mt-4 text-white font-[Outfit] text-lg">{x.t}</div>
                <div className="mt-2 text-slate-400 text-sm">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
